import { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { Card, Input } from "@/components/shared";
import { DollarSign, Search, Clock } from "lucide-react";

const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;

export default function PayRates() {
  const [rates, setRates] = useState([]);
  const [q, setQ] = useState("");
  useEffect(() => { api.get("/my-pay-rates").then((r) => setRates(r.data)).catch(() => {}); }, []);

  const filtered = useMemo(() => rates.filter((r) => !q || `${r.service} ${r.category}`.toLowerCase().includes(q.toLowerCase())), [rates, q]);
  const groups = useMemo(() => {
    const g = {};
    filtered.forEach((r) => { (g[r.category] = g[r.category] || []).push(r); });
    return Object.entries(g);
  }, [filtered]);

  return (
    <div className="space-y-6">
      <div className="animate-rise">
        <div className="font-mono text-xs uppercase tracking-widest text-[#FF5722] mb-1">What You Earn</div>
        <h1 className="font-head font-black uppercase text-3xl sm:text-4xl text-white tracking-tight">My Pay Rates</h1>
        <p className="text-zinc-500 text-sm mt-1 font-mono">Estimated contractor pay per service — a blended average across clients. Actual pay per job varies by client.</p>
      </div>

      <Card className="p-4 border-l-4 border-[#FF5722]" data-testid="payrates-note">
        <div className="flex items-start gap-2">
          <Clock size={16} className="text-[#FF5722] mt-0.5 shrink-0" />
          <p className="text-sm text-zinc-300">Pay varies per client. You are paid no more than <span className="text-white font-semibold">45 days</span> after the work order is completed with <span className="text-white font-semibold">all required photos and checklist items</span>. Complete every task within the timeframe to avoid reassignment and late fees.</p>
        </div>
      </Card>

      <div className="relative max-w-md">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
        <Input data-testid="payrates-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search services…" className="pl-9" />
      </div>

      {groups.length === 0 ? (
        <Card className="p-10 text-center text-zinc-600 font-mono"><DollarSign className="inline mr-2" size={16} />No rates available yet.</Card>
      ) : groups.map(([cat, rows]) => (
        <Card key={cat} className="p-0 overflow-hidden" data-testid={`payrate-cat-${cat}`}>
          <div className="bg-zinc-900 px-4 py-2 font-mono text-[11px] uppercase tracking-widest text-[#FF5722] border-b border-zinc-800">{cat}</div>
          <div className="divide-y divide-zinc-800">
            {rows.map((r, i) => (
              <div key={i} data-testid={`payrate-${i}`} className="flex items-center gap-3 px-4 py-2.5 text-sm">
                <div className="flex-1 min-w-0"><div className="text-white truncate">{r.service}</div><div className="font-mono text-[10px] text-zinc-600">per {r.unit}{r.clients > 1 ? ` · ${r.clients} clients` : ""}</div></div>
                <div className="text-right"><div className="font-mono text-emerald-400 text-base">{money(r.blended_pay)}</div>{r.min_pay !== r.max_pay && <div className="font-mono text-[9px] text-zinc-600">{money(r.min_pay)}–{money(r.max_pay)}</div>}</div>
              </div>
            ))}
          </div>
        </Card>
      ))}
    </div>
  );
}
